<template>
  <section
    class="cmp"
    :class="{ 'cmp--min': options.minimized, 'cmp--wide': wide }"
    role="dialog"
    :aria-label="heading"
    @keydown.esc.stop="minimize"
  >
    <header class="cmp__head" @click="options.minimized && restore()">
      <strong class="cmp__heading">{{ heading }}</strong>
      <span v-if="savedAt && !options.minimized" class="cmp__saved">{{ t('mailClient.compose.savedAt', { time: savedAt }) }}</span>
      <button type="button" class="cmp__icon" :title="options.minimized ? t('mailClient.compose.restore') : t('mailClient.compose.minimize')" @click.stop="options.minimized ? restore() : minimize()">
        <svg viewBox="0 0 24 24"><path :d="options.minimized ? 'M6 15l6-6 6 6' : 'M6 12h12'" /></svg>
      </button>
      <button type="button" class="cmp__icon cmp__only-wide" :title="wide ? t('mailClient.compose.shrink') : t('mailClient.compose.expand')" @click.stop="wide = !wide">
        <svg viewBox="0 0 24 24"><path :d="wide ? 'M9 4v5H4M20 9h-5V4M15 20v-5h5M4 15h5v5' : 'M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5'" /></svg>
      </button>
      <button type="button" class="cmp__icon" :title="t('mailClient.compose.close')" @click.stop="requestClose">
        <svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6 6 18" /></svg>
      </button>
    </header>

    <div v-show="!options.minimized" class="cmp__body">
      <!-- Cargando lo que se prepara (la ficha, el borrador, la cuenta). -->
      <!-- Con la forma del redactor: De, Para y Asunto con sus rótulos de
           verdad (no son datos) y el alto de sus renglones, y el editor con su
           barra y su alto mínimo. Al llegar, los campos caen en su sitio. -->
      <div v-if="loading" class="cmp__sk" role="status" :aria-label="t('common.loading')" aria-busy="true">
        <div class="cmp__fields" aria-hidden="true">
          <div class="cmp__from">
            <span class="cmp__label">{{ t('mailClient.compose.from') }}</span>
            <UiSkeleton width="55%" />
          </div>
          <div class="cmp__from">
            <span class="cmp__label">{{ t('mailClient.compose.to') }}</span>
            <UiSkeleton width="40%" />
          </div>
          <div class="cmp__subject">
            <span class="cmp__label">{{ t('mailClient.compose.subject') }}</span>
            <UiSkeleton width="65%" />
          </div>
        </div>
        <div class="cmp__sk-editor" aria-hidden="true">
          <UiSkeleton variant="block" height="28px" />
          <UiSkeleton variant="block" :height="wide ? '320px' : '200px'" />
        </div>
      </div>

      <!-- Sin cuenta: no se puede escribir desde ninguna parte. -->
      <UiEmpty v-else-if="!accounts.length" :title="t('mailClient.compose.noAccountTitle')" :description="t('mailClient.compose.noAccountBody')">
        <UiButton @click="goToAccounts">{{ t('mailClient.accounts.connect') }}</UiButton>
      </UiEmpty>

      <UiEmpty v-else-if="loadError" :title="t('mailClient.compose.loadErrorTitle')" :description="loadError">
        <UiButton variant="secondary" @click="init">{{ t('mailClient.retry') }}</UiButton>
      </UiEmpty>

      <template v-else>
        <div class="cmp__fields">
          <div class="cmp__from">
            <span class="cmp__label">{{ t('mailClient.compose.from') }}</span>
            <UiSelect
              v-model="accountId"
              class="cmp__select"
              :aria-label="t('mailClient.compose.from')"
              :options="accounts.map(account => ({ value: account.id, label: account.displayName ? `${account.displayName} <${account.address}>` : account.address }))"
              :searchable="false"
            />
          </div>
          <div class="cmp__line">
            <RecipientInput ref="toField" v-model="to" class="grow" :label="t('mailClient.compose.to')" :scope="options.context" @next="focusSubject" />
            <span class="cmp__toggles">
              <button v-if="!showCc" type="button" class="cmp__toggle" @click="showCc = true">{{ t('mailClient.compose.cc') }}</button>
              <button v-if="!showBcc" type="button" class="cmp__toggle" @click="showBcc = true">{{ t('mailClient.compose.bcc') }}</button>
            </span>
          </div>
          <RecipientInput v-if="showCc" v-model="cc" :label="t('mailClient.compose.cc')" :scope="options.context" @next="focusSubject" />
          <RecipientInput v-if="showBcc" v-model="bcc" :label="t('mailClient.compose.bcc')" :scope="options.context" @next="focusSubject" />
          <div class="cmp__subject">
            <span class="cmp__label">{{ t('mailClient.compose.subject') }}</span>
            <!-- Intro en el asunto pasa al texto: en el teléfono es la tecla «Siguiente». -->
            <input
              ref="subjectField" v-model="subject" type="text" maxlength="500" enterkeyhint="next"
              :aria-label="t('mailClient.compose.subject')"
              @keydown.enter.prevent="editor?.focus()"
            >
          </div>
        </div>

        <!-- El idioma en que se le escribe: lo ofrece el módulo cuando tiene el
             texto y el PDF en varios (una factura a un cliente francés). -->
        <div v-if="variants.length > 1" class="cmp__variants">
          <span class="cmp__links-label">{{ t('mailClient.compose.language') }}</span>
          <UiSegmented :model-value="variant" :options="variants.map(item => ({ id: item.id, label: item.label }))" @update:model-value="useVariant" />
        </div>

        <!-- Con qué fichas del ERP quedará relacionada la conversación. -->
        <div v-if="links.length" class="cmp__links">
          <span class="cmp__links-label">{{ t('mailClient.compose.linkedTo') }}</span>
          <span v-for="link in links" :key="link.type + link.referenceId" class="cmp__link">{{ link.label || link.type }}</span>
        </div>

        <div class="cmp__editor">
          <RichEditor
            ref="editor"
            v-model="html"
            :placeholder="t('mailClient.compose.bodyPlaceholder')"
            :min-height="wide ? '320px' : '200px'"
            @files="upload"
          />
        </div>

        <AiPanel
          v-if="showAi"
          class="cmp__ai"
          :html="html"
          :message-id="options.message?.id || ''"
          :context="options.context || null"
          :replying="replying"
          @replace="editor.setHtml($event)"
          @insert="editor.insertHtml($event)"
          @close="showAi = false"
        />

        <label v-if="signature" class="cmp__sign">
          <input v-model="withSignature" type="checkbox">
          <span>{{ t('mailClient.compose.signature') }}</span>
        </label>
        <!-- eslint-disable-next-line vue/no-v-html — the account's own signature, cleaned on save and here -->
        <div v-if="signature && withSignature" class="cmp__signature" v-html="signature" />

        <div v-if="quote" class="cmp__quote">
          <button type="button" class="cmp__toggle" @click="showQuote = !showQuote">
            {{ showQuote ? t('mailClient.compose.hideOriginal') : t('mailClient.compose.showOriginal') }}
          </button>
          <MailFrame v-if="showQuote" :html="quote" :title="t('mailClient.compose.original')" />
        </div>

        <!-- Adjuntos: los de la ficha, los reenviados y los que se suben. -->
        <ul v-if="allFiles.length" class="cmp__files">
          <li v-for="file in contextFiles" :key="`c-${file.key}`" class="cmp__file" :class="{ off: !file.on }">
            <input v-model="file.on" type="checkbox" :aria-label="file.filename">
            <span class="cmp__fname">{{ file.filename }}</span>
            <span class="cmp__fmeta">{{ t('mailClient.compose.fromRecord') }}</span>
          </li>
          <li v-for="file in forwarded" :key="`f-${file.index}`" class="cmp__file" :class="{ off: !file.on }">
            <input v-model="file.on" type="checkbox" :aria-label="file.filename">
            <span class="cmp__fname">{{ file.filename }}</span>
            <span class="cmp__fmeta">{{ size(file.size) }}</span>
          </li>
          <li v-for="(file, index) in uploads" :key="`u-${index}`" class="cmp__file" :class="{ bad: file.error }">
            <span class="cmp__fname">{{ file.filename }}</span>
            <span class="cmp__fmeta">{{ file.error || (file.id ? size(file.size) : t('mailClient.compose.uploading')) }}</span>
            <button type="button" class="cmp__x" :aria-label="t('mailClient.compose.removeFile', { name: file.filename })" @click="removeUpload(index)">×</button>
          </li>
        </ul>
        <p v-if="tooBig" class="cmp__warn">{{ t('mailClient.compose.tooBig') }}</p>

        <p v-if="sendError" class="cmp__error" role="alert">{{ sendError }}</p>

        <footer class="cmp__foot">
          <UiButton :loading="sending" :disabled="uploading || tooBig" @click="send">
            {{ sending ? t('mailClient.compose.sending') : t('mailClient.compose.send') }}
          </UiButton>
          <label class="cmp__attach" :title="t('mailClient.compose.attach')">
            <input type="file" multiple @change="upload([...$event.target.files]); $event.target.value = ''">
            <svg viewBox="0 0 24 24"><path d="m21 12-8.5 8.5a5 5 0 0 1-7-7L14 5a3.5 3.5 0 0 1 5 5l-8.5 8.5a2 2 0 0 1-3-3L15 8" /></svg>
          </label>
          <button v-if="canAi" type="button" class="cmp__icon cmp__ai-btn" :class="{ on: showAi }" :title="t('mailClient.ai.title')" @click="showAi = !showAi">
            <svg viewBox="0 0 24 24"><path d="M12 3l1.8 4.7L18.5 9.5l-4.7 1.8L12 16l-1.8-4.7L5.5 9.5l4.7-1.8z" /></svg>
            <span>{{ t('mailClient.ai.button') }}</span>
          </button>
          <span class="grow" />
          <UiButton class="cmp__only-desk" variant="ghost" size="sm" :loading="saving" @click="saveDraft(false)">{{ t('mailClient.compose.saveDraft') }}</UiButton>
          <!-- En el teléfono el texto no cabe junto a «Enviar», el clip y la IA:
               el mismo botón como icono. -->
          <button type="button" class="cmp__icon cmp__only-phone" :title="t('mailClient.compose.saveDraft')" :aria-label="t('mailClient.compose.saveDraft')" :disabled="saving" @click="saveDraft(false)">
            <svg viewBox="0 0 24 24"><path d="M5 3h11l3 3v13a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2zM7 3v6h9V3M7 21v-7h10v7" /></svg>
          </button>
          <button type="button" class="cmp__icon" :title="t('mailClient.compose.discard')" @click="discard">
            <svg viewBox="0 0 24 24"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" /></svg>
          </button>
        </footer>
      </template>
    </div>
  </section>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import {
  UiSelect,
  UiButton, UiEmpty, UiSegmented, UiSkeleton, useConfirm, useToast, errorMessage,
} from '@general/ui-kit'
import { useAuthStore } from '@general/auth/frontend'

import { mailApi } from './api'
import { useMailStore } from './store'
import { sanitizeHtml, textToHtml } from './sanitize'
import { replyRecipients, replySubject, quoteHtml, assemble, isAddress, withJoinButton } from './compose.mjs'
import RichEditor from './RichEditor.vue'
import RecipientInput from './RecipientInput.vue'
import AiPanel from './AiPanel.vue'
import MailFrame from './MailFrame.vue'

/**
 * Writing an email: new, a reply, a forward, a reopened draft, or «Send by
 * email» from a record of the ERP (an invoice with its PDF, an order to its
 * supplier). From one of the person's own accounts, always.
 *
 * What it does on its own and what it never does:
 *  - it saves a draft on the server every half minute while something changed,
 *    so closing the tab or losing the network does not lose the text;
 *  - it adds the account's signature under the text, and changes it when the
 *    account changes;
 *  - it quotes the original in a reply, drawn in a sandboxed frame;
 *  - it NEVER sends without the Send button — not the AI, not a shortcut.
 */
const props = defineProps({
  options: { type: Object, required: true },
})
const store = useMailStore()
const auth = useAuthStore()
const router = useRouter()
const toast = useToast()
const { confirm } = useConfirm()
const { t, te, locale } = useI18n()

const AUTOSAVE_MS = 30_000
const MAX_BYTES = 20 * 1024 * 1024

const loading = ref(true)
const loadError = ref('')
const accounts = ref([])
const accountId = ref('')
const to = ref([])
const cc = ref([])
const bcc = ref([])
const showCc = ref(false)
const showBcc = ref(false)
const subject = ref('')
const html = ref('')
const quote = ref('')
const showQuote = ref(false)
const withSignature = ref(true)
const links = ref([])
const contextFiles = ref([])
const forwarded = ref([])
const uploads = ref([])
const draftId = ref(props.options.draftId || '')
const replyTo = ref('')
const showAi = ref(false)
const wide = ref(false)
const sending = ref(false)
const saving = ref(false)
const sendError = ref('')
const savedAt = ref('')
const editor = ref(null)
const toField = ref(null)
const subjectField = ref(null)
let pristine = ''
let timer = null

// The same email in several languages, offered by the module that prepares it:
// [{ id, label, subject, html, options }]. Changing it rewrites only what the
// variant wrote — what somebody typed on top is theirs.
const variants = computed(() => (Array.isArray(props.options.variants) ? props.options.variants : []))
const variant = ref('')
let fromVariant = { subject: '', html: '' }

function useVariant(id) {
  const chosen = variants.value.find(item => item.id === id)
  if (!chosen) return
  variant.value = id
  if (!subject.value.trim() || subject.value === fromVariant.subject) subject.value = chosen.subject ?? ''
  const clean = sanitizeHtml(chosen.html ?? '')
  if (!html.value.trim() || html.value === fromVariant.html) editor.value ? editor.value.setHtml(clean) : (html.value = clean)
  fromVariant = { subject: chosen.subject ?? '', html: clean }
  // What the module's PDF needs in that language (its words, its locale).
  if (props.options.context && chosen.options) props.options.context.options = chosen.options
}

const replying = computed(() => ['reply', 'replyAll'].includes(props.options.mode))
const canAi = computed(() => auth.can?.('ai:use') ?? false)
const account = computed(() => accounts.value.find(item => item.id === accountId.value) ?? null)
const signature = computed(() => sanitizeHtml(account.value?.signature || ''))
const uploading = computed(() => uploads.value.some(file => !file.id && !file.error))
const allFiles = computed(() => [...contextFiles.value, ...forwarded.value, ...uploads.value])
const bytes = computed(() => uploads.value.reduce((sum, file) => sum + (file.size || 0), 0)
  + forwarded.value.filter(file => file.on).reduce((sum, file) => sum + (file.size || 0), 0))
const tooBig = computed(() => bytes.value > MAX_BYTES)
const heading = computed(() => {
  if (subject.value) return subject.value
  return t(`mailClient.compose.titles.${props.options.mode || 'new'}`)
})

const snapshot = () => JSON.stringify([accountId.value, to.value, cc.value, bcc.value, subject.value, html.value, uploads.value.map(file => file.id)])
const dirty = () => snapshot() !== pristine

// ── Starting ─────────────────────────────────────────────────────────────────

async function init() {
  loading.value = true
  loadError.value = ''
  try {
    const data = await mailApi.accounts()
    accounts.value = data.accounts ?? []
    if (!accounts.value.length) return
    const opts = props.options
    accountId.value = opts.accountId || opts.message?.accountId || accounts.value.find(item => item.isDefault)?.id || accounts.value[0].id
    if (!accounts.value.some(item => item.id === accountId.value)) accountId.value = accounts.value[0].id

    if (opts.draftId) await fromDraft(opts.draftId)
    else if (opts.message && ['reply', 'replyAll', 'forward'].includes(opts.mode)) fromMessage(opts.message, opts.mode)
    if (variants.value.length) {
      const first = variants.value.find(item => item.id === opts.variant) ?? variants.value[0]
      variant.value = first.id
      subject.value = first.subject ?? ''
      html.value = sanitizeHtml(first.html ?? '')
      fromVariant = { subject: subject.value, html: html.value }
      if (opts.context && first.options) opts.context.options = first.options
    }
    if (opts.context) await fromContext(opts.context)
    if (opts.to) to.value = normalise(opts.to)
    if (opts.subject) subject.value = opts.subject
    if (opts.html) html.value = sanitizeHtml(opts.html)
  } catch (failure) {
    loadError.value = errorMessage(failure, t)
  } finally {
    loading.value = false
    await nextTick()
    pristine = snapshot()
    if (!loadError.value && accounts.value.length) {
      if (!to.value.length) toField.value?.focus()
      else editor.value?.focus()
    }
  }
}

function focusSubject() {
  subjectField.value?.focus()
}

function normalise(list) {
  return (list ?? []).map(entry => (typeof entry === 'string' ? { name: '', address: entry } : { name: entry.name ?? '', address: entry.address ?? entry.email ?? '' }))
    .filter(entry => entry.address)
}

async function fromDraft(id) {
  const { draft } = await mailApi.draft(id)
  accountId.value = draft.accountId || accountId.value
  to.value = normalise(draft.to)
  cc.value = normalise(draft.cc)
  showCc.value = cc.value.length > 0
  subject.value = draft.subject
  html.value = sanitizeHtml(draft.html)
  replyTo.value = draft.replyTo || ''
  withSignature.value = false
}

function fromMessage(message, mode) {
  const own = accounts.value.map(item => item.address)
  const people = replyRecipients(message, mode, own)
  to.value = people.to
  cc.value = people.cc
  showCc.value = cc.value.length > 0
  subject.value = replySubject(message.subject, mode)
  const original = message.html ? sanitizeHtml(message.html) : textToHtml(message.text)
  quote.value = quoteHtml(message, mode, {
    wrote: t('mailClient.compose.wrote'),
    forwarded: t('mailClient.compose.forwardedHeader'),
    from: t('mailClient.compose.from'),
    date: t('mailClient.reader.date'),
    subject: t('mailClient.compose.subject'),
    to: t('mailClient.compose.to'),
  }, original, message.date ? new Date(message.date).toLocaleString(locale.value, { dateStyle: 'full', timeStyle: 'short' }) : '')
  if (mode === 'forward') {
    forwarded.value = (message.attachments ?? []).filter(file => !file.inline && file.downloadable)
      .map(file => ({ ...file, on: true }))
  } else {
    replyTo.value = message.id
  }
}

async function fromContext(context) {
  const { context: prepared } = await mailApi.context(context.type, context.referenceId, context.options ?? null)
  if (!to.value.length) to.value = normalise(prepared.to)
  if (!cc.value.length && prepared.cc?.length) { cc.value = normalise(prepared.cc); showCc.value = true }
  if (!subject.value) {
    subject.value = prepared.subjectKey && te(prepared.subjectKey) ? t(prepared.subjectKey, prepared.subjectParams ?? {}) : prepared.subject
  }
  if (!html.value && prepared.html) html.value = sanitizeHtml(prepared.html)
  // A meeting's invitation gets its «Join» button in the reader's language:
  // the calendar writes the link and no words (§16). Not on a follow-up.
  if (context.type === 'calendar_event' && !context.options?.followUp) {
    html.value = withJoinButton(html.value, t('mailClient.meetings.join'))
  }
  contextFiles.value = (prepared.attachments ?? []).map(file => ({ ...file, on: true }))
  links.value = prepared.links ?? []
}


// ── Attachments ──────────────────────────────────────────────────────────────

async function upload(files) {
  for (const file of files) {
    const entry = reactive({ filename: file.name, size: file.size, id: '', error: '' })
    uploads.value.push(entry)
    if (file.size > MAX_BYTES) { entry.error = t('mailClient.compose.fileTooBig'); continue }
    try {
      const saved = await mailApi.upload(file)
      entry.id = saved.file?.id ?? saved.id
    } catch (failure) {
      entry.error = errorMessage(failure, t)
    }
  }
}

async function removeUpload(index) {
  const [file] = uploads.value.splice(index, 1)
  if (file?.id) mailApi.removeUpload(file.id).catch(() => {})
}

const size = (value) => {
  const n = Number(value) || 0
  if (n > 1048576) return `${(n / 1048576).toFixed(1)} MB`
  return `${Math.max(1, Math.round(n / 1024))} kB`
}

// ── Sending ──────────────────────────────────────────────────────────────────

function body() {
  return assemble(html.value, { signature: withSignature.value ? signature.value : '', quote: quote.value })
}

function payload() {
  return {
    accountId: accountId.value,
    to: to.value,
    cc: cc.value,
    bcc: bcc.value,
    subject: subject.value,
    html: body(),
    fileIds: uploads.value.filter(file => file.id).map(file => file.id),
    replyTo: replyTo.value || undefined,
    draftId: draftId.value || undefined,
    forward: props.options.mode === 'forward' && props.options.message && forwarded.value.some(file => file.on)
      ? { messageId: props.options.message.id, indexes: forwarded.value.filter(file => file.on).map(file => file.index) }
      : undefined,
    context: props.options.context
      ? { ...props.options.context, attachments: contextFiles.value.filter(file => file.on).map(file => file.key) }
      : undefined,
  }
}

async function send() {
  sendError.value = ''
  toField.value?.commit?.()
  await nextTick()
  const everyone = [...to.value, ...cc.value, ...bcc.value]
  if (!everyone.length) { sendError.value = t('mailClient.compose.needRecipient'); return }
  const bad = everyone.find(entry => !isAddress(entry.address))
  if (bad) { sendError.value = t('mailClient.compose.badRecipient', { who: bad.address }); return }
  if (!subject.value.trim() && !(await confirm({ title: t('mailClient.compose.noSubjectTitle'), message: t('mailClient.compose.noSubjectBody'), confirmLabel: t('mailClient.compose.sendAnyway') }))) return
  sending.value = true
  try {
    const result = await mailApi.send(payload())
    clearInterval(timer)
    toast.success(t('mailClient.compose.sent'))
    props.options.onSent?.(result)
    store.touched()
    store.close()
  } catch (failure) {
    sendError.value = errorMessage(failure, t)
  } finally {
    sending.value = false
  }
}

async function saveDraft(silent) {
  if (!accountId.value || saving.value || sending.value) return
  saving.value = true
  try {
    const saved = await mailApi.saveDraft({ ...payload(), html: body(), bcc: bcc.value })
    draftId.value = saved.id
    savedAt.value = new Date().toLocaleTimeString(locale.value, { hour: '2-digit', minute: '2-digit' })
    pristine = snapshot()
    store.touched()
    if (!silent) toast.success(t('mailClient.compose.draftSaved'))
  } catch (failure) {
    if (!silent) toast.error(errorMessage(failure, t))
  } finally {
    saving.value = false
  }
}

async function autosave() {
  if (!dirty() || sending.value) return
  if (!html.value.trim() && !to.value.length && !subject.value.trim()) return
  await saveDraft(true)
}

// ── Leaving ──────────────────────────────────────────────────────────────────

function minimize() {
  props.options.minimized = true
}
function restore() {
  props.options.minimized = false
}

async function requestClose() {
  if (dirty() && (html.value.trim() || to.value.length || subject.value.trim())) {
    const keep = await confirm({
      title: t('mailClient.compose.closeTitle'),
      message: t('mailClient.compose.closeBody'),
      confirmLabel: t('mailClient.compose.saveDraft'),
      cancelLabel: t('mailClient.compose.discard'),
    })
    if (keep) await saveDraft(true)
    else if (draftId.value) await dropDraft()
  }
  clearInterval(timer)
  store.close()
}

async function discard() {
  if ((html.value.trim() || draftId.value) && !(await confirm({ title: t('mailClient.compose.discardTitle'), message: t('mailClient.compose.discardBody'), danger: true, confirmLabel: t('mailClient.compose.discard') }))) return
  if (draftId.value) await dropDraft()
  for (const file of uploads.value) if (file.id) mailApi.removeUpload(file.id).catch(() => {})
  clearInterval(timer)
  store.close()
}

async function dropDraft() {
  try {
    await mailApi.act({ ids: [draftId.value], action: 'delete' })
    store.touched()
  } catch { /* already gone */ }
}

function goToAccounts() {
  store.close()
  router.push('/mail/accounts')
}

function beforeUnload(event) {
  if (dirty() && (html.value.trim() || to.value.length)) {
    event.preventDefault()
    event.returnValue = ''
  }
}

watch(accountId, () => { sendError.value = '' })

/** For the host, when another composer is asked for: keep this one as a draft. */
async function putAway() {
  if (dirty() && (html.value.trim() || to.value.length || subject.value.trim())) await saveDraft(true)
  clearInterval(timer)
}
defineExpose({ putAway })

onMounted(() => {
  init()
  timer = setInterval(autosave, AUTOSAVE_MS)
  window.addEventListener('beforeunload', beforeUnload)
})
onBeforeUnmount(() => {
  clearInterval(timer)
  window.removeEventListener('beforeunload', beforeUnload)
})
</script>

<style scoped>
.cmp {
  position: fixed;
  z-index: 150;
  right: 24px;
  /* Sobre el teclado, si lo hay (ui-kit utils/viewport.js): vive en <body>. */
  bottom: var(--keyboard-inset, 0px);
  width: min(640px, calc(100vw - 32px));
  max-height: calc(var(--app-height, 100dvh) - 80px);
  display: flex;
  flex-direction: column;
  border: 1px solid var(--color-border);
  border-bottom: 0;
  border-radius: var(--radius-lg) var(--radius-lg) 0 0;
  background: var(--color-surface);
  box-shadow: var(--shadow-lg);
}
.cmp--wide {
  right: 50%;
  bottom: 50%;
  transform: translate(50%, 50%);
  width: min(1000px, calc(100vw - 48px));
  height: min(860px, calc(var(--app-height, 100dvh) - 48px));
  max-height: none;
  border-bottom: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
}
.cmp--min { width: 320px; max-height: none; height: auto; transform: none; right: 24px; bottom: var(--keyboard-inset, 0px); border-radius: var(--radius-lg) var(--radius-lg) 0 0; }
.cmp__head {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: 8px 8px 8px 16px;
  border-radius: var(--radius-lg) var(--radius-lg) 0 0;
  background: var(--color-gray-800);
  color: #fff;
}
.cmp--min .cmp__head { cursor: pointer; }
.cmp__heading { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: var(--text-sm); }
.cmp__saved { font-size: var(--text-xs); opacity: .75; white-space: nowrap; }
.cmp__head .cmp__icon { color: #fff; }
.cmp__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: 30px;
  height: 30px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--color-text-soft);
  cursor: pointer;
}
.cmp__icon:hover { background: rgba(127, 127, 127, .18); }
.cmp__icon svg, .cmp__attach svg { width: 18px; height: 18px; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
.cmp__body { display: flex; flex-direction: column; gap: var(--space-2); padding: 0 16px 12px; overflow-y: auto; min-height: 0; flex: 1; }
.cmp__sk { display: flex; flex-direction: column; gap: var(--space-2); }
.cmp__sk-editor { display: flex; flex-direction: column; gap: 6px; margin-top: var(--space-1); }
.cmp__fields { display: flex; flex-direction: column; }
.cmp__from, .cmp__subject { display: flex; align-items: center; min-height: 36px; border-bottom: 1px solid var(--color-border); }
.cmp__label { width: 52px; flex-shrink: 0; color: var(--color-text-soft); font-size: var(--text-sm); }
.cmp__subject input {
  flex: 1;
  min-width: 0;
  height: 32px;
  border: 0;
  outline: none;
  background: transparent;
  color: var(--color-text);
  font-size: var(--text-sm);
}
/* The ERP's dropdown on the «From» line, which has no box: like the «To» and
   «Subject» lines under it. The list that opens is the kit's. */
.cmp__select { flex: 1; min-width: 0; }
.cmp__select :deep(.select__trigger) {
  height: 32px;
  padding: 0 4px;
  border-color: transparent;
  background: transparent;
  font-size: var(--text-sm);
  box-shadow: none;
}
.cmp__select :deep(.select__trigger:hover) { border-color: transparent; background: var(--color-surface-2); }
.cmp__subject input { font-weight: 600; }
.cmp__line { display: flex; align-items: flex-start; }
.cmp__toggles { display: flex; gap: 4px; padding-top: 8px; }
.cmp__toggle { border: 0; background: none; color: var(--color-primary); font-size: var(--text-xs); cursor: pointer; padding: 2px 4px; }
.cmp__links { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; font-size: var(--text-xs); }
.cmp__variants { display: flex; flex-wrap: wrap; align-items: center; gap: var(--space-2); }
.cmp__links-label { color: var(--color-text-soft); }
.cmp__link { padding: 2px 8px; border-radius: 999px; background: var(--color-surface-2); color: var(--color-text); border: 1px solid var(--color-border); }
.cmp__editor { display: flex; flex-direction: column; min-height: 0; }
.cmp__sign { display: flex; align-items: center; gap: 6px; font-size: var(--text-xs); color: var(--color-text-soft); }
.cmp__signature { padding: 4px 12px; border-left: 2px solid var(--color-border); color: var(--color-text-soft); font-size: 13px; }
.cmp__quote { display: flex; flex-direction: column; align-items: flex-start; gap: 4px; }
.cmp__quote :deep(.mframe) { width: 100%; }
.cmp__files { display: flex; flex-wrap: wrap; gap: 6px; list-style: none; margin: 0; padding: 0; }
.cmp__file {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 280px;
  padding: 4px 6px 4px 10px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  background: var(--color-surface-2);
  font-size: var(--text-xs);
}
.cmp__file.off { opacity: .55; }
.cmp__file.bad { border-color: var(--color-danger); color: var(--color-danger); }
.cmp__fname { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-weight: 600; }
.cmp__fmeta { color: var(--color-text-soft); white-space: nowrap; }
.cmp__x { border: 0; background: none; cursor: pointer; font-size: 15px; color: var(--color-text-soft); }
.cmp__warn, .cmp__error { margin: 0; font-size: var(--text-sm); }
.cmp__warn { color: var(--color-warning); }
.cmp__error { color: var(--color-danger); }
.cmp__foot { display: flex; align-items: center; gap: var(--space-2); padding-top: var(--space-2); border-top: 1px solid var(--color-border); position: sticky; bottom: -12px; background: var(--color-surface); padding-bottom: 4px; }
.cmp__attach { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; border-radius: 6px; color: var(--color-text-soft); cursor: pointer; }
.cmp__attach:hover { background: var(--color-gray-100); }
/* Escondido sin `display: none`: algún Safari de iOS no abre el selector de
   archivos desde la etiqueta de un campo que no se pinta. */
.cmp__attach { position: relative; overflow: hidden; }
.cmp__attach input { position: absolute; width: 1px; height: 1px; opacity: 0; pointer-events: none; }
.cmp__only-phone { display: none; }
.cmp__ai-btn { padding: 0 10px; font-size: var(--text-sm); }
.cmp__ai-btn svg { fill: currentColor; stroke: none; }
.cmp__ai-btn.on { background: var(--color-primary-soft); color: var(--color-primary); }
.grow { flex: 1; }

@media (max-width: 760px) {
  /* La pantalla que se ve y no la ventana: con el teclado fuera en iOS, `inset: 0`
     dejaba el cuerpo y el pie con «Enviar» debajo de las teclas. */
  .cmp, .cmp--wide { inset: 0; top: var(--viewport-top, 0px); bottom: auto; height: var(--app-height, 100dvh); width: 100%; max-height: none; transform: none; border-radius: 0; }
  .cmp--min { inset: auto 0 var(--keyboard-inset, 0px) 0; height: auto; width: 100%; }
  .cmp__head { border-radius: 0; padding-top: max(8px, env(safe-area-inset-top)); }
  .cmp__only-wide { display: none; }
  .cmp__only-desk { display: none; }
  .cmp__only-phone { display: inline-flex; }

  /* Como el redactor de Gmail o de Outlook: el texto se queda con lo que sobra
     y la barra de «Enviar» abajo del todo, justo encima del teclado si lo hay
     (el redactor mide lo que se ve). Antes la barra quedaba a media pantalla
     tras un texto corto, con un hueco vacío debajo. */
  .cmp__body { padding: 0 12px; }
  .cmp__editor { flex: 1 0 auto; }
  .cmp__editor :deep(.rte) { flex: 1; }
  .cmp__foot { margin-top: auto; bottom: 0; gap: 4px; padding-bottom: max(6px, env(safe-area-inset-bottom)); }
  .cmp__file { max-width: 100%; }
}
/* Con el teclado fuera el borde de abajo lo pone el teclado, no el iPhone. */
@media (max-width: 760px) {
  html.keyboard-open .cmp__foot { padding-bottom: 6px; }
}

/* Con el dedo, el asunto a 16px: más pequeño, iOS amplía la página al enfocarlo
   y no la devuelve (ver --text-field en ui-kit tokens.css). */
@media (pointer: coarse) {
  .cmp__subject input { font-size: var(--text-field); height: 40px; }
  .cmp__from, .cmp__subject { min-height: 44px; }
  /* Lo que se toca con el pulgar, a 44 px; la cruz de un adjunto a 32. */
  .cmp__icon { min-width: 44px; height: 44px; }
  .cmp__attach { width: 44px; height: 44px; }
  .cmp__toggle { min-height: 40px; padding: 0 8px; font-size: var(--text-sm); }
  .cmp__toggles { padding-top: 2px; }
  .cmp__x { min-width: 32px; min-height: 32px; }
  .cmp__file { padding-block: 2px; }
  .cmp__sign { min-height: 36px; }
}
</style>
